import { OFFERED_BEHAVIORS } from '../../stores/utils/column-property-spec';
// TYPE-ONLY: the runtime store drags the Lit design system into node-env
// unit tests.
import type { TableRow } from '../../stores/table-store';
import {
  normVar,
  splitList,
} from '../table/node-property-panel/ai-action-assist/action-assist-model';

/**
 * Project Bobcat — the solution assistant's node proposals
 * (behind the master). The model proposes content for any node;
 * this checks every proposed value against the REAL solution before the
 * designer sees it, and turns the fields they keep into the row update Apply
 * writes. Pure, so it tests in node.
 */

export type ProposalColumn =
  | 'nodeName'
  | 'message'
  | 'utterances'
  | 'nextNodes'
  | 'richAssetType'
  | 'richAssetContent'
  | 'behaviors'
  | 'answerRequired'
  | 'description'
  | 'command'
  | 'nodeInput'
  | 'parameterInput'
  | 'output'
  | 'decisionVariable'
  | 'whatsNext'
  | 'variables';

export interface ProposalField {
  column: ProposalColumn;
  value: string;
  why: string;
}

/** What the chat route returns for one node. */
export interface NodeProposalDraft {
  /** An existing node's number; empty for a node to be created. */
  nodeNumber: string;
  fields: ProposalField[];
  /** A node that does not exist yet (the chat route's `NodeProposalCreate`). */
  create?: { agent: string; nodeType: 'D' | 'A'; ref?: string };
}

/**
 * Where a proposed NEW node goes: an existing agent (its group row id), or a
 * new agent of that name — created on Apply, not before.
 */
export type ProposalCreateTarget = (
  | { agent: string; agentId: string }
  | { agent: string; agentId: null }
) & {
  /** The name other new nodes route to it by ("@n1"), until it has a number. */
  ref?: string;
};

export type ProposalNodeKind = 'decision' | 'action';

/** A proposal that passed validation: every field fits this solution. */
export interface NodeProposal {
  /**
   * The node's row id; for a node to be created, a placeholder
   * (`new:<n>`) that keys the review card until Apply creates the row.
   */
  rowId: string;
  /** Set when the node is new: the agent it goes into. */
  create?: ProposalCreateTarget;
  nodeNumber: string;
  nodeName: string;
  kind: ProposalNodeKind;
  fields: ProposalField[];
  /** Anything dropped or trimmed, said plainly for the review. */
  notes: string[];
}

export const DECISION_COLUMNS: readonly ProposalColumn[] = [
  'nodeName',
  'message',
  'utterances',
  'nextNodes',
  'richAssetType',
  'richAssetContent',
  'behaviors',
  'answerRequired',
  'variables',
  'description',
];

export const ACTION_COLUMNS: readonly ProposalColumn[] = [
  'nodeName',
  'command',
  'description',
  'nodeInput',
  'parameterInput',
  'output',
  'decisionVariable',
  'whatsNext',
  'variables',
];

/** Review-card labels. */
export const PROPOSAL_COLUMN_LABELS: Record<ProposalColumn, string> = {
  nodeName: 'Node Name',
  message: 'Message',
  utterances: 'Example Phrases',
  nextNodes: 'Next Nodes',
  richAssetType: 'Rich Asset Type',
  richAssetContent: 'Rich Asset Content',
  behaviors: 'Behaviors',
  answerRequired: 'Answer Required',
  description: 'Description',
  command: 'Command',
  nodeInput: 'Node Input',
  parameterInput: 'Parameter Input',
  output: 'Output',
  decisionVariable: 'Decision Variable',
  whatsNext: 'What Next?',
  variables: 'Variable',
};

/** The fields of a row this module reads — a structural slice of TableRow. */
export interface ProposalRow {
  id: string;
  nodeNumber?: number | string | null;
  nodeName?: unknown;
  nodeType?: unknown;
  /** The type an existing node's rich asset has, for content proposed alone. */
  richAssetType?: unknown;
  /** A group row's name lives here — how a new node finds its agent. */
  settings?: { groupName?: unknown } | null;
}

/** The existing agent (group row) with this name, matched case-blind. */
export const findAgentByName = (
  rows: ProposalRow[],
  name: string
): ProposalRow | undefined => {
  const want = name.trim().toLowerCase();
  return rows.find(
    (r) =>
      String(r.nodeType ?? '').toLowerCase() === 'g' &&
      String(r.settings?.groupName ?? '')
        .trim()
        .toLowerCase() === want
  );
};

/** Agents a proposal may never create a second one of. */
const CORE_AGENT_NAMES = ['supervisor agent', 'knowledge agent'];

const kindOf = (row: ProposalRow): ProposalNodeKind | null => {
  const t = String(row.nodeType ?? '').toLowerCase();
  if (t === 'd') return 'decision';
  if (t === 'a') return 'action';
  return null;
};

const columnsFor = (kind: ProposalNodeKind) =>
  kind === 'decision' ? DECISION_COLUMNS : ACTION_COLUMNS;

const nodeNumbersOf = (rows: ProposalRow[]): Set<string> =>
  new Set(
    rows
      .filter((r) => kindOf(r) && r.nodeNumber != null)
      .map((r) => String(r.nodeNumber))
  );

/**
 * One field checked against the solution. Returns the value to keep (possibly
 * trimmed of bad parts) and a note when something was dropped, or `null` when
 * nothing usable is left.
 */
const checkField = (
  field: ProposalField,
  known: Set<string>,
  commands: Set<string>
): { value: string | null; note?: string } => {
  const label = PROPOSAL_COLUMN_LABELS[field.column];
  switch (field.column) {
    case 'command': {
      const cmd = field.value.trim();
      if (commands.has(cmd)) return { value: cmd };
      return {
        value: null,
        note: `Command ${cmd} isn't uploaded in this version — upload its script in Actions first, then set it.`,
      };
    }
    case 'nextNodes': {
      const nums = splitList(field.value).map((n) =>
        n.trim().replace(/^#/, '')
      );
      const good = nums.filter((n) => known.has(n));
      const bad = nums.filter((n) => !known.has(n));
      return {
        value: good.length ? good.join(',') : null,
        ...(bad.length
          ? {
              note: `${label}: dropped ${bad.join(', ')} — not in this solution or this flow.`,
            }
          : {}),
      };
    }
    case 'whatsNext': {
      const pairs = field.value
        .split('|')
        .map((p) => p.trim())
        .filter(Boolean);
      const good: string[] = [];
      const bad: string[] = [];
      pairs.forEach((pair) => {
        const [outcome, dest] = pair.split('~').map((s) => s?.trim() ?? '');
        const num = dest?.replace(/^#/, '');
        if (outcome && num && known.has(num)) good.push(`${outcome}~${num}`);
        else bad.push(pair);
      });
      return {
        value: good.length ? good.join('|') : null,
        ...(bad.length
          ? {
              note: `${label}: dropped ${bad.join(', ')} — each needs outcome~node, with a node in this solution.`,
            }
          : {}),
      };
    }
    case 'behaviors': {
      const items = splitList(field.value).map((b) => b.trim().toLowerCase());
      const good = items.filter((b) => OFFERED_BEHAVIORS.includes(b));
      const bad = items.filter((b) => !OFFERED_BEHAVIORS.includes(b));
      return {
        value: good.length ? good.join(',') : null,
        ...(bad.length
          ? { note: `${label}: dropped ${bad.join(', ')} — not a behavior.` }
          : {}),
      };
    }
    case 'answerRequired': {
      const v = field.value.trim().toLowerCase();
      if (v === 'true' || v === 'false') return { value: v };
      return {
        value: null,
        note: `${label}: "${field.value}" isn't true/false.`,
      };
    }
    case 'parameterInput': {
      try {
        const parsed: unknown = JSON.parse(field.value);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return { value: JSON.stringify(parsed, null, 2) };
        }
      } catch {
        // falls through to the note
      }
      return {
        value: null,
        note: `${label} wasn't a JSON object, so it was left out.`,
      };
    }
    case 'variables':
    case 'nodeInput': {
      const v = splitList(field.value).map(normVar).filter(Boolean).join(',');
      return { value: v || null };
    }
    case 'output': {
      const v = splitList(field.value).map(normVar).filter(Boolean).join(',');
      return { value: v || null };
    }
    case 'decisionVariable': {
      const v = normVar(field.value);
      return { value: v || null };
    }
    case 'utterances': {
      const v = field.value
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)
        .join('\n');
      return { value: v || null };
    }
    default:
      return { value: field.value.trim() || null };
  }
};

/**
 * The rich asset types a proposal may set. persistent_menu is left out: it
 * belongs only on the -411 system node, which the designer sets up.
 */
export const PROPOSABLE_ASSET_TYPES = [
  'button',
  'buttons',
  'selection',
  'listpicker',
  'quick_reply',
  'imagebutton',
  'carousel',
  'star',
  'image',
  'png',
  'video',
  'document',
  'webview',
  'secure',
  'datepicker',
  'location',
  'file_upload',
] as const;

/** Static menus carry each option's destination inside their content. */
const ROUTING_ASSET_TYPES = new Set([
  'button',
  'buttons',
  'selection',
  'listpicker',
  'quick_reply',
  'imagebutton',
  'carousel',
  'star',
]);

const JSON_ASSET_TYPES = new Set([
  'buttons',
  'listpicker',
  'quick_reply',
  'imagebutton',
  'carousel',
  'star',
  'image',
  'file_upload',
]);

const isDynamicContent = (content: string): boolean =>
  /^\{[^{}]*\*[^{}]*\}$/.test(content.trim()) ||
  /"type"\s*:\s*"dynamic"/.test(content);

/** Every destination a rich asset's content routes to. */
export const richAssetDests = (type: string, content: string): string[] => {
  if (type === 'button' || type === 'selection') {
    return content
      .split('|')
      .map((pair) => pair.split('~')[1]?.trim() ?? '')
      .filter(Boolean);
  }
  return [...content.matchAll(/"dest"\s*:\s*"?(@?[\w-]+)"?/g)].map((m) =>
    m[1].replace(/^#/, '')
  );
};

/**
 * A proposed rich asset, checked as a pair: the type is one a proposal may
 * set, the content is in that type's format (JSON for the JSON types) and
 * every destination in it is a real node, and Next Nodes follows the rule —
 * blank beside a static menu (its options carry the routing), ONE node
 * otherwise. Mutates `fields`; says what it dropped in `notes`.
 */
const checkRichAsset = (
  fields: ProposalField[],
  { row, known }: { row: ProposalRow | undefined; known: Set<string> },
  notes: string[]
): void => {
  const drop = (column: ProposalColumn) => {
    const i = fields.findIndex((f) => f.column === column);
    if (i >= 0) fields.splice(i, 1);
  };
  const typeField = fields.find((f) => f.column === 'richAssetType');
  const contentField = fields.find((f) => f.column === 'richAssetContent');
  if (typeField) {
    typeField.value = typeField.value.trim().toLowerCase();
    if (
      !(PROPOSABLE_ASSET_TYPES as readonly string[]).includes(typeField.value)
    ) {
      notes.push(
        `Rich Asset Type: "${typeField.value}" isn't a type that can be proposed, so the rich asset was left out.`
      );
      drop('richAssetType');
      drop('richAssetContent');
      return;
    }
  }
  const rowType = String(row?.richAssetType ?? '').toLowerCase();
  const type =
    typeField?.value ??
    (rowType && rowType !== 'none' && rowType !== 'text' ? rowType : '');
  if (contentField) {
    if (!type) {
      notes.push('Rich Asset Content came without a type, so it was left out.');
      drop('richAssetContent');
    } else if (!isDynamicContent(contentField.value)) {
      let ok = true;
      if (JSON_ASSET_TYPES.has(type)) {
        try {
          JSON.parse(contentField.value);
        } catch {
          ok = false;
          notes.push(
            `Rich Asset Content isn't valid JSON for ${type}, so it was left out.`
          );
        }
      }
      const bad = ok
        ? richAssetDests(type, contentField.value).filter((d) => !known.has(d))
        : [];
      if (bad.length) {
        ok = false;
        notes.push(
          `Rich Asset Content routes to ${bad.join(', ')}, not in this solution, so it was left out.`
        );
      }
      if (!ok) drop('richAssetContent');
    }
  }
  // A menu type without usable options would seed a default menu pointing
  // nowhere (or mismatch the content the node has), so it goes too.
  if (
    typeField &&
    ROUTING_ASSET_TYPES.has(type) &&
    !fields.some((f) => f.column === 'richAssetContent')
  ) {
    if (!contentField) {
      notes.push(
        `Rich Asset Type ${type} came without its options, so it was left out.`
      );
    }
    drop('richAssetType');
  }
  const next = fields.find((f) => f.column === 'nextNodes');
  const staticMenu =
    ROUTING_ASSET_TYPES.has(type) &&
    fields.some(
      (f) => f.column === 'richAssetContent' && !isDynamicContent(f.value)
    );
  if (next && staticMenu) {
    notes.push(
      'Next Nodes: left out — a menu carries its destinations in its options.'
    );
    drop('nextNodes');
  } else if (next) {
    const nums = splitList(next.value);
    if (nums.length > 1) {
      next.value = nums[0];
      notes.push(
        `Next Nodes holds one node, so it kept ${nums[0]} and dropped ${nums
          .slice(1)
          .join(', ')}.`
      );
    }
  }
};

/**
 * Keeps only what fits the solution: nodes that exist (group rows excluded),
 * columns that belong to the node's type, commands the version has, and
 * routing to real nodes. A node left with nothing usable is dropped with a
 * note, so the designer hears why rather than seeing nothing.
 */
export const validateNodeProposals = (
  drafts: NodeProposalDraft[],
  rows: ProposalRow[],
  {
    commands,
    maxNodes = 10,
    knownVars = [],
  }: {
    commands: Iterable<string>;
    maxNodes?: number;
    /** Variables the solution already names (`knownVariables`). */
    knownVars?: Iterable<string>;
  }
): { proposals: NodeProposal[]; notes: string[] } => {
  const known = nodeNumbersOf(rows);
  // New nodes in this answer route to each other as "@ref".
  drafts.forEach((d) => {
    if (d.create?.ref) known.add(`@${d.create.ref}`);
  });
  const commandSet = new Set(commands);
  const byNumber = new Map<string, ProposalRow>();
  rows.forEach((r) => {
    if (
      kindOf(r) &&
      r.nodeNumber != null &&
      !byNumber.has(String(r.nodeNumber))
    ) {
      byNumber.set(String(r.nodeNumber), r);
    }
  });

  const proposals: NodeProposal[] = [];
  const notes: string[] = [];
  const seen = new Set<string>();

  let newCount = 0;
  drafts.forEach((draft) => {
    const { create } = draft;
    const num = create
      ? ''
      : String(draft.nodeNumber ?? '')
          .replace(/^#/, '')
          .trim();
    if (!create) {
      if (seen.has(num)) return;
      seen.add(num);
    }
    if (proposals.length >= maxNodes) {
      notes.push(
        create
          ? `Left out a new node for ${create.agent}: at most ${maxNodes} nodes per request.`
          : `Left out #${num}: at most ${maxNodes} nodes per request.`
      );
      return;
    }
    let row: ProposalRow | undefined;
    let kind: ProposalNodeKind | null;
    let target: ProposalCreateTarget | undefined;
    if (create) {
      kind = create.nodeType === 'A' ? 'action' : 'decision';
      const existing = findAgentByName(rows, create.agent);
      if (
        !existing &&
        CORE_AGENT_NAMES.includes(create.agent.trim().toLowerCase())
      ) {
        notes.push(
          `Didn't propose a second ${create.agent}: a solution has one.`
        );
        return;
      }
      target = existing
        ? { agent: create.agent, agentId: existing.id }
        : { agent: create.agent, agentId: null };
      if (create.ref) target.ref = create.ref;
    } else {
      row = byNumber.get(num);
      kind = row ? kindOf(row) : null;
      if (!row || !kind) {
        notes.push(
          `Node ${num} isn't in this solution, so nothing was proposed for it.`
        );
        return;
      }
    }
    const allowed = new Set(columnsFor(kind));
    const nodeNotes: string[] = [];
    const fields: ProposalField[] = [];
    const cols = new Set<string>();
    draft.fields.forEach((f) => {
      if (cols.has(f.column)) return;
      if (!allowed.has(f.column)) {
        nodeNotes.push(
          `${PROPOSAL_COLUMN_LABELS[f.column] ?? f.column} doesn't apply to a ${kind} node.`
        );
        return;
      }
      const { value, note } = checkField(f, known, commandSet);
      if (note) nodeNotes.push(note);
      if (value === null) return;
      cols.add(f.column);
      fields.push({ ...f, value });
    });
    if (kind === 'decision') checkRichAsset(fields, { row, known }, nodeNotes);
    // A new node is worth creating even before it has content; an existing
    // one with nothing usable proposed is not worth a card.
    if (!fields.length && !target) {
      notes.push(`Nothing usable was proposed for ${num}.`, ...nodeNotes);
      return;
    }
    const order = columnsFor(kind);
    fields.sort((a, b) => order.indexOf(a.column) - order.indexOf(b.column));
    if (target) {
      newCount += 1;
      proposals.push({
        rowId: `new:${newCount}`,
        create: target,
        nodeNumber: 'New',
        nodeName:
          fields.find((f) => f.column === 'nodeName')?.value ??
          `${target.agent} node`,
        kind,
        fields,
        notes: nodeNotes,
      });
      return;
    }
    proposals.push({
      rowId: (row as ProposalRow).id,
      nodeNumber: num,
      nodeName:
        typeof row?.nodeName === 'string'
          ? row.nodeName
          : String(row?.nodeName ?? ''),
      kind,
      fields,
      notes: nodeNotes,
    });
  });

  checkProposalVariables(proposals, knownVars);
  return { proposals, notes };
};

const VAR_READ = /\{([A-Z][A-Z0-9_]*)\}/g;

const READ_COLUMNS: readonly ProposalColumn[] = [
  'message',
  'parameterInput',
  'nodeInput',
];

/**
 * The variables side of a proposed flow, checked so the designer doesn't find
 * out on a live chat: an action's Variable column gets every variable its
 * inputs read (the engine passes an action only the variables listed there),
 * and a node that reads a variable nothing sets — not the solution, not
 * another node in this flow — says so on its card.
 */
export const checkProposalVariables = (
  proposals: NodeProposal[],
  knownVars: Iterable<string>
): void => {
  const fieldOf = (p: NodeProposal, column: ProposalColumn) =>
    p.fields.find((f) => f.column === column);
  const listed = (p: NodeProposal) =>
    splitList(fieldOf(p, 'variables')?.value ?? '')
      .map(normVar)
      .filter(Boolean);
  const set = new Set<string>([...knownVars].map(normVar));
  proposals.forEach((p) => listed(p).forEach((v) => set.add(v)));

  proposals.forEach((p) => {
    const reads = new Set<string>();
    READ_COLUMNS.forEach((column) => {
      const text = fieldOf(p, column)?.value ?? '';
      for (const m of text.matchAll(VAR_READ)) reads.add(m[1]);
    });
    if (!reads.size) return;

    if (p.kind === 'action') {
      const have = new Set(listed(p));
      const missing = [...reads].filter((v) => !have.has(v));
      if (missing.length) {
        const all = [...have, ...missing].join(',');
        const field = fieldOf(p, 'variables');
        if (field) field.value = all;
        else
          p.fields.push({
            column: 'variables',
            value: all,
            why: 'Lists every variable this action reads, so it receives them.',
          });
        const order = columnsFor('action');
        p.fields.sort(
          (a, b) => order.indexOf(a.column) - order.indexOf(b.column)
        );
        p.notes.push(
          `Added ${missing.join(', ')} to Variable: an action only receives the variables listed there.`
        );
      }
    }

    const unset = [...reads].filter((v) => !set.has(v));
    if (unset.length) {
      p.notes.push(
        `Reads ${unset.map((v) => `{${v}}`).join(', ')}, which nothing in the solution or this flow sets yet.`
      );
    }
  });
};

/** The current value of a column as panel text — for "new" vs "replaces". */
export const currentProposalText = (
  row: Record<string, unknown>,
  column: ProposalColumn
): string => {
  const v = row[column];
  if (v == null) return '';
  if (Array.isArray(v)) {
    return v
      .map(String)
      .filter(Boolean)
      .join(column === 'whatsNext' ? '|' : ',');
  }
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') {
    const d = (v as { default?: unknown }).default;
    if (typeof d === 'string') return d;
    try {
      return JSON.stringify(v);
    } catch {
      return '';
    }
  }
  return String(v);
};

/** The value committed for each kept field, merged into one row update. The
 *  column conversion is the node panel's own (`buildPanelCellCommitUpdate`),
 *  injected so this stays free of the store. */
export const buildProposalUpdates = (
  proposal: NodeProposal,
  keep: Record<string, boolean>,
  commitUpdate: (column: ProposalColumn, text: string) => Partial<TableRow>
): Partial<TableRow> => {
  const updates: Partial<TableRow> = {};
  proposal.fields
    .filter((f) => keep[f.column] !== false)
    .forEach((f) => Object.assign(updates, commitUpdate(f.column, f.value)));
  return updates;
};

/**
 * A routing value with its "@ref" destinations (new nodes in the same flow)
 * swapped for the node numbers they got when created. A destination not
 * created yet is left out rather than written as a dangling "@ref".
 */
export const resolveProposalRefs = (
  column: ProposalColumn,
  value: string,
  numberOf: (ref: string) => string | undefined
): { value: string; unresolved: string[] } => {
  const unresolved: string[] = [];
  const one = (dest: string): string | null => {
    const d = dest.trim();
    if (!d.startsWith('@')) return d;
    const num = numberOf(d.slice(1));
    if (num) return num;
    unresolved.push(d);
    return null;
  };
  if (column === 'nextNodes') {
    const out = splitList(value)
      .map(one)
      .filter((n): n is string => !!n);
    return { value: out.join(','), unresolved };
  }
  if (column === 'richAssetContent') {
    // "Label~@n2" (button, selection) and "dest": "@n2" (the JSON types). A
    // menu can't be written with an option pointing nowhere, so content with
    // a destination not created yet is left out whole.
    const before = unresolved.length;
    const out = value
      .replace(/~\s*(@[\w-]+)/g, (m, ref: string) => {
        const n = one(ref);
        return n ? `~${n}` : m;
      })
      .replace(
        /("dest"\s*:\s*)"?(@[\w-]+)"?/g,
        (m, key: string, ref: string) => {
          const n = one(ref);
          return n ? `${key}${/^-?\d+$/.test(n) ? n : JSON.stringify(n)}` : m;
        }
      );
    return { value: unresolved.length > before ? '' : out, unresolved };
  }
  if (column === 'whatsNext') {
    const out = value
      .split('|')
      .map((pair) => {
        const [outcome, dest] = pair.split('~');
        const n = dest === undefined ? null : one(dest);
        return n ? `${outcome.trim()}~${n}` : null;
      })
      .filter((p): p is string => !!p);
    return { value: out.join('|'), unresolved };
  }
  return { value, unresolved };
};

/** Every field kept by default, as the Assisted build review starts. */
export const defaultKeep = (proposal: NodeProposal): Record<string, boolean> =>
  Object.fromEntries(proposal.fields.map((f) => [f.column, true]));
